import React, { useState, useEffect } from 'react';
import {
  TabList,
  Tab,
  SelectTabData,
  SelectTabEvent,
  Link,
  makeStyles,
  shorthands,
  tokens,
  Avatar,
  Badge,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
  Switch,
} from '@fluentui/react-components';
import { ChevronUpRegular, DocumentRegular } from '@fluentui/react-icons';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import DataTable, { ColumnConfig } from '../components/DataTable';
import ShareDialog from '../components/ShareDialog';
import { useSelection } from '../contexts/SelectionContext';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { getRowMenuForRoute } from '../config/toolbars';
import { BOOQABLE_ADMIN_URL } from '../config/booqable';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDate } from '../lib/businessTime';

interface Document {
  id: string;
  booqable_id?: string | null;
  number: string;
  isProForma?: boolean;
  order: string;
  customer: string;
  invoiceDate?: string;
  exclTaxes: number;
  inclTaxes: number;
  deposit?: number;
  due?: number;
  paymentStatus?: 'paid' | 'payment_due';
  confirmed?: boolean;
  icon?: never; // Placeholder for icon column
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableSurface: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
    height: 'calc(100vh - 200px)',
  },
  tabListContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('16px', '16px', '0', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  filterToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  metricsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: '20px',
    marginBottom: '12px',
  },
  hideMetricsButton: {
    fontSize: '14px',
    color: '#0078d4',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('0'),
    fontFamily: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  metricsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '20px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  metricCard: {
    backgroundColor: '#ffffff',
    ...shorthands.border('1px', 'solid', '#e0e0e0'),
    borderRadius: '8px',
    ...shorthands.padding('20px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  metricLabel: {
    fontSize: '14px',
    color: '#605e5c',
    fontWeight: 400,
  },
  metricValue: {
    fontSize: '32px',
    fontWeight: 600,
    color: '#242424',
  },
});

type TabValue = 'invoices' | 'contracts' | 'quotes';

const Documents: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);

  // Map URL parameter values to tab values
  const urlToTabMap: Record<string, TabValue> = {
    'invoice': 'invoices',
    'contract': 'contracts',
    'quote': 'quotes',
  };

  const tabToUrlMap: Record<TabValue, string> = {
    'invoices': 'invoice',
    'contracts': 'contract',
    'quotes': 'quote',
  };

  // Initialize selected tab from URL parameter or default to 'invoices'
  const getInitialTab = (): TabValue => {
    const typeParam = searchParams.get('type');
    if (typeParam && urlToTabMap[typeParam]) {
      return urlToTabMap[typeParam];
    }
    return 'invoices';
  };

  const [selectedTab, setSelectedTab] = useState<TabValue>(getInitialTab());
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<Document | null>(null);
  const [showProForma, setShowProForma] = useState(false);
  const { setSelectedCount, setOnClearSelection } = useSelection();

  // Update selected tab when URL parameter changes
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam && urlToTabMap[typeParam]) {
      setSelectedTab(urlToTabMap[typeParam]);
    }
  }, [searchParams]);

  // Create fetch function for documents
  const createFetchFunction = (documentType: string) => async () => {
    const url = `/booqable/documents?document_type=${documentType}`;
    console.log('Fetching documents from:', url);

    const response = await apiFetch(url);
    const data = await response.json();

    console.log('Documents API response:', data);
    console.log('Number of documents:', data.data?.length || 0);

    if (data.data && data.data.length > 0) {
      console.log('First document sample:', data.data[0]);
    }

    // Map Booqable documents to our Document interface
    const mappedDocuments = (data.data || []).map((item: any) => {
      const attrs = item.attributes;
      console.log('Mapping document:', item.id, attrs);

      return {
        id: item.id,
        number: attrs.number || null,
        isProForma: !attrs.finalized || !attrs.number,
        order: attrs.order_number || 'N/A',
        customer: attrs.customer_name || 'Unknown',
        invoiceDate: attrs.created_at ? formatBusinessDate(attrs.created_at) : '',
        exclTaxes: (attrs.total_in_cents || 0) / 100,
        inclTaxes: (attrs.grand_total_in_cents || 0) / 100,
        deposit: (attrs.deposit_in_cents || 0) / 100,
        due: (attrs.to_be_paid_in_cents || 0) / 100,
        paymentStatus: (attrs.to_be_paid_in_cents || 0) === 0 ? 'paid' : 'payment_due',
        confirmed: attrs.status === 'confirmed',
      };
    });

    console.log('Mapped documents:', mappedDocuments.length);
    return mappedDocuments;
  };

  // One React Query per tab. `enabled` keeps the inactive tabs idle so we don't
  // burn API calls until the user clicks them; switching tabs flips the flag and
  // React Query auto-fetches.
  const invoicesQuery = useQuery({
    queryKey: queryKeys.documents.byType('invoice'),
    queryFn: createFetchFunction('invoice'),
    enabled: selectedTab === 'invoices',
    staleTime: 5 * 60 * 1000,
  });

  const contractsQuery = useQuery({
    queryKey: queryKeys.documents.byType('contract'),
    queryFn: createFetchFunction('contract'),
    enabled: selectedTab === 'contracts',
    staleTime: 5 * 60 * 1000,
  });

  const quotesQuery = useQuery({
    queryKey: queryKeys.documents.byType('quote'),
    queryFn: createFetchFunction('quote'),
    enabled: selectedTab === 'quotes',
    staleTime: 5 * 60 * 1000,
  });

  const currentQuery = selectedTab === 'invoices' ? invoicesQuery :
    selectedTab === 'contracts' ? contractsQuery :
      quotesQuery;

  const allDocuments = currentQuery.data || [];
  const loading = currentQuery.isLoading;

  // Filter out pro forma invoices unless toggle is on
  const documents = selectedTab === 'invoices' && !showProForma
    ? allDocuments.filter((doc: Document) => !doc.isProForma)
    : allDocuments;

  // Update selection context when selection changes
  useEffect(() => {
    setSelectedCount(selectedDocumentIds.length);
  }, [selectedDocumentIds, setSelectedCount]);

  // Set the clear selection callback
  useEffect(() => {
    setOnClearSelection(() => () => setSelectedDocumentIds([]));
  }, [setOnClearSelection]);

  // Clear selection context on unmount
  useEffect(() => {
    return () => {
      setSelectedCount(0);
    };
  }, [setSelectedCount]);

  // Listen for share document events
  useEffect(() => {
    const handleShareDocument = (event: CustomEvent) => {
      const doc = event.detail;
      setDocumentToShare(doc);
      setShareDialogOpen(true);
    };

    window.addEventListener('shareDocument', handleShareDocument as EventListener);
    return () => {
      window.removeEventListener('shareDocument', handleShareDocument as EventListener);
    };
  }, []);

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    const newTab = data.value as TabValue;
    setSelectedTab(newTab);
    setSelectedDocumentIds([]); // Clear selection when switching tabs

    // Update URL parameter
    setSearchParams({ type: tabToUrlMap[newTab] });
  };

  const handleRowClick = (doc: any) => {
    navigate(`/documents/${doc.id}`);
  };

  // Get row menu configuration for documents page
  const rowMenuItems = getRowMenuForRoute(location.pathname);

  // Define columns for invoices
  const invoiceColumns: ColumnConfig<Document>[] = [
    {
      columnKey: 'checkbox',
      label: '',
      width: '50px',
    },
    {
      columnKey: 'number',
      label: '#',
      sortable: true,
      width: '120px',
      render: (doc) =>
        !doc.number || doc.number === '' ? (
          <span style={{ fontSize: '14px', color: '#605e5c' }}>Draft invoice</span>
        ) : (
          doc.number
        ),
    },
    {
      columnKey: 'order',
      label: 'Order',
      sortable: true,
      width: '100px',
    },
    {
      columnKey: 'customer',
      label: 'Customer',
      sortable: true,
      render: (doc) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar name={doc.customer} color="colorful" size={32} />
          <span>{doc.customer}</span>
        </div>
      ),
    },
    {
      columnKey: 'invoiceDate',
      label: 'Invoice date',
      sortable: true,
      render: (doc) => doc.invoiceDate || '',
    },
    {
      columnKey: 'exclTaxes',
      label: 'Excl. taxes',
      sortable: true,
      render: (doc) => `$${doc.exclTaxes.toFixed(2)}`,
    },
    {
      columnKey: 'inclTaxes',
      label: 'Incl. taxes',
      sortable: true,
      render: (doc) => `$${doc.inclTaxes.toFixed(2)}`,
    },
    {
      columnKey: 'due',
      label: 'Due',
      sortable: true,
      render: (doc) => `$${doc.due?.toFixed(2) || '0.00'}`,
    },
    {
      columnKey: 'paymentStatus',
      label: 'Payment status',
      sortable: false,
      render: (doc) => (
        <Badge
          appearance="filled"
          color={doc.paymentStatus === 'paid' ? 'success' : 'warning'}
        >
          {doc.paymentStatus === 'paid' ? 'Paid' : 'Payment due'}
        </Badge>
      ),
    },
  ];

  // Define columns for contracts and quotes
  const contractQuoteColumns: ColumnConfig<Document>[] = [
    {
      columnKey: 'checkbox',
      label: '',
      width: '50px',
    },
    {
      columnKey: 'number',
      label: '#',
      sortable: true,
      render: (doc) =>
        doc.number === 'Draft invoice' ? (
          <span style={{ fontSize: '14px', color: '#605e5c' }}>Draft invoice</span>
        ) : (
          doc.number
        ),
    },
    {
      columnKey: 'order',
      label: 'Order',
      sortable: true,
      groupable: true,
    },
    {
      columnKey: 'customer',
      label: 'Customer',
      sortable: true,
      render: (doc) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar name={doc.customer} color="colorful" size={32} />
          <span>{doc.customer}</span>
        </div>
      ),
    },
    {
      columnKey: 'confirmed',
      label: 'Confirmed',
      sortable: true,
      render: (doc) => (
        <Badge appearance="filled" color="success">
          {doc.confirmed ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      columnKey: 'invoiceDate',
      label: 'Invoice date',
      sortable: true,
      render: (doc) => doc.invoiceDate || '',
    },
    {
      columnKey: 'exclTaxes',
      label: 'Excl. taxes',
      sortable: true,
      render: (doc) => `$${doc.exclTaxes.toFixed(2)}`,
    },
    {
      columnKey: 'inclTaxes',
      label: 'Incl. taxes',
      sortable: true,
      render: (doc) => `$${doc.inclTaxes.toFixed(2)}`,
    },
  ];

  const columns = selectedTab === 'invoices' ? invoiceColumns : contractQuoteColumns;

  // Metrics data based on the screenshot
  const metrics = {
    invoices: {
      count: 5,
      paidAmount: 750.00,
      due: 8600.00,
      revenue: 9350.00,
    },
    contracts: {
      count: 0,
      paidAmount: 0,
      due: 0,
      revenue: 0,
    },
    quotes: {
      count: 0,
      paidAmount: 0,
      due: 0,
      revenue: 0,
    },
  };

  const currentMetrics = metrics[selectedTab];

  return (
    <div className={styles.container} style={{ paddingTop: '16px' }}>
      <div className={styles.tableSurface}>
        <div className={styles.tabListContainer}>
          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="invoices">Invoices</Tab>
            <Tab value="contracts">Contracts</Tab>
            <Tab value="quotes">Quotes</Tab>
          </TabList>
          {selectedTab === 'invoices' && (
            <div className={styles.filterToggle}>
              <Switch
                checked={showProForma}
                onChange={(_, data) => setShowProForma(data.checked)}
              />
              <span>Show draft invoices</span>
            </div>
          )}
        </div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            Loading documents...
          </div>
        ) : documents.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', flexDirection: 'column', alignItems: 'center' }}>
            <p>No {selectedTab} found.</p>
            <p style={{ fontSize: '12px', color: '#605e5c' }}>Check the browser console for API response details.</p>
          </div>
        ) : (
          <DataTable
            data={documents}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="documents"
            itemsPerPage={10}
            showCheckboxes={true}
            onRowClick={handleRowClick}
            onSelectionChange={setSelectedDocumentIds}
            selectedKeys={selectedDocumentIds}
            disableWrapper={true}
            rowMenuItems={rowMenuItems || undefined}
          />
        )}
      </div>

      {/* Share Document Dialog */}
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setDocumentToShare(null);
        }}
        itemType="document"
        itemName={documentToShare?.number || documentToShare?.order || ''}
        shareUrl={`${BOOQABLE_ADMIN_URL}/documents/${documentToShare?.booqable_id || documentToShare?.id}`}
        onCopyLink={() => {
          dispatchToast(
            <Toast>
              <ToastTitle>Link copied</ToastTitle>
              <ToastBody>Share link has been copied to clipboard.</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
      />

      <Toaster toasterId={toasterId} />
    </div>
  );
};

export default Documents;